import { ChatBubbleIcon, ExitIcon, ExternalLinkIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { useAuth } from '@/auth/auth-provider';
import { AssistantSettingsDialog } from '@/components/assistant/settings-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TimeZonePref } from '@/lib/format';
import { useStripedRows } from '@/lib/table-prefs';
import { useTimeZonePref } from '@/lib/time-zone';
import { useTheme } from '../theme/theme-provider';
import type { ThemeMode } from '../theme/theme-mode';

const initials = (name: string) =>
  name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

export function UserMenu() {
  const { session, logout, can } = useAuth();
  const { mode, setMode } = useTheme();
  const [tz, setTz] = useTimeZonePref();
  const [striped, setStriped] = useStripedRows();
  const [assistantOpen, setAssistantOpen] = useState(false);
  if (!session) return null;
  const { fullName, email } = session.user;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Account: ${fullName}`} className="rounded-full">
            <Avatar className="size-8">
              <AvatarFallback>{initials(fullName || email)}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="font-normal">
            <div className="truncate text-sm font-medium">{fullName}</div>
            <div className="truncate text-xs text-muted-foreground">{email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={mode} onValueChange={(v) => setMode(v as ThemeMode)}>
            <DropdownMenuRadioItem value="system">Follow system</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">Times</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={tz} onValueChange={(v) => setTz(v as TimeZonePref)}>
            <DropdownMenuRadioItem value="utc">UTC</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="local">Local time</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">Tables</DropdownMenuLabel>
          <DropdownMenuCheckboxItem checked={striped} onCheckedChange={(on) => setStriped(on)}>
            Alternating rows
          </DropdownMenuCheckboxItem>
          {/* The analytics assistant, set per device (it needs analytics access). */}
          {can('VIEW_LIST_GAMES') && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setAssistantOpen(true)}>
                <ChatBubbleIcon /> Assistant…
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <a href="/">
              <ExternalLinkIcon /> Current backoffice
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void logout()}>
            <ExitIcon /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AssistantSettingsDialog open={assistantOpen} onOpenChange={setAssistantOpen} />
    </>
  );
}
